// apps/cli/src/commands/bootstrap/ui.ts
import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors } from "../../lib/output.js";
import { getAzAccount } from "../../lib/auth.js";
import { readBootstrapState } from "../../lib/bootstrap-state.js";
import {
  resolveFoundryEndpointWithWait,
  getSignedInUserOid,
  ensureFounderFoundryRole,
  writeWebEnvLocal,
  installWebDeps,
  runInherit,
  assertNodeVersion,
  serveOnboardingRelayDetached,
  serveOnboardingUiDetached,
  stopOnboardingUi,
  autoOpenOnboardingUi,
  deployIntakeAgentWithRetry,
  buildIntakeFieldOverrides,
} from "../../lib/bootstrap-ui.js";
import { getSignedInUserIdentity, composeFounderIdentityNote } from "../../lib/founder-identity.js";
import { resolveIntakeModel } from "../../lib/intake-model.js";
import { resolveSubscriptionInventory } from "../../lib/subscription-inventory.js";
import { INTAKE_AGENT } from "../../lib/intake-agent.js";

type DeployOutcome =
  | { ok: true; version: string }
  | { ok: false; error: Error };

function renderDeploySuccess(version: string, envPath: string): string {
  return `${colors.success("✓")} Azzy is live (${INTAKE_AGENT} v${version}).\n` +
    `  env: ${envPath}\n` +
    `  Opening ${colors.field("http://localhost:3000")} in your browser → Sign in with Microsoft → chat with Azzy.\n` +
    `  ${colors.dim("(If it doesn't open, browse to http://localhost:3000 yourself.)")}\n` +
    `  ${colors.dim("First turn may say 'warming up' for a few minutes while access propagates — that's expected.")}\n`;
}

function renderDeployFailure(error: Error): string {
  const hint = error instanceof LocalCliError && error.hint
    ? error.hint
    : "Re-run 'm8t bootstrap ui' (idempotent).";
  return `${colors.error("✗")} Azzy deploy failed: ${error.message}\n` +
    `  ${colors.hint(hint)}\n`;
}

export class BootstrapUiCommand extends M8tCommand {
  static paths = [["bootstrap", "ui"]];
  static usage = Command.Usage({
    description: "Deploy Azzy + start the local onboarding chat UI in the background (returns immediately).",
    details: [
      "Run after `m8t bootstrap launch`, in parallel with `status --watch`. Waits for the cloud",
      "installer's foundry-create phase, grants you Foundry data-plane access, deploys the no-brain",
      "onboarding intake agent (azzy-intake), writes apps/web/.env.local (voice relay config is added",
      "only when --voice is set — see --voice for what that does and does not affect),",
      "installs deps, and starts the webapp detached so a coding agent can continue without opening",
      "a separate terminal.",
      "",
      "The command returns as soon as the server is ready (or after ~45 s if it is still compiling).",
      "Use `--stop` to shut it down, `--foreground` to keep the old blocking behaviour.",
    ].join("\n"),
    examples: [
      ["Start the onboarding UI (backgrounds the server, returns)", "$0 bootstrap ui --repo-root /path/to/m8t"],
      ["Stop the running onboarding UI", "$0 bootstrap ui --stop"],
      ["Prep only (don't serve at all)", "$0 bootstrap ui --repo-root /path/to/m8t --prep-only"],
      ["Foreground (blocking, old behaviour)", "$0 bootstrap ui --repo-root /path/to/m8t --foreground"],
      ["Experimental: start the voice relay (no effect on the text-only intake)", "$0 bootstrap ui --repo-root /path/to/m8t --voice"],
    ],
  });

  repoRoot = Option.String("--repo-root");
  port = Option.String("--port", "3000");
  endpoint = Option.String("--endpoint", {
    description: "Foundry project endpoint to target — disambiguates when the subscription has multiple projects.",
  });
  prepOnly = Option.Boolean("--prep-only", false);
  skipInstall = Option.Boolean("--skip-install", false);
  stop = Option.Boolean("--stop", false);
  foreground = Option.Boolean("--foreground", false);
  voice = Option.Boolean("--voice", false, {
    description:
      "Experimental: when serving, starts the voice relay and writes the intake voice env var. The onboarding intake is text-only and is unaffected by this flag — no voice worker is registered for it.",
  });

  async executeCommand(): Promise<number> {
    // --stop is completely independent: it doesn't need bootstrap state or Foundry.
    if (this.stop === true) {
      const stopped = stopOnboardingUi();
      if (stopped) {
        this.context.stdout.write(`${colors.success("✓")} stopped the onboarding chat UI.\n`);
      } else {
        this.context.stdout.write(`  ${colors.dim("nothing to stop (no onboarding-ui.pid found).")}\n`);
      }
      return 0;
    }

    const state = await readBootstrapState();
    if (!state) {
      throw new LocalCliError({
        code: "BOOTSTRAP_NO_STATE",
        message: "No bootstrap is in flight (no ~/.m8t/bootstrap.json).",
        hint: "Run 'm8t bootstrap launch --location <region>' first.",
      });
    }
    const repoRoot = (typeof this.repoRoot === "string" ? this.repoRoot : undefined) ?? process.cwd();

    if (this.port !== "3000") {
      throw new LocalCliError({
        code: "BOOTSTRAP_UI_PORT_MSAL_LOCKED",
        message: `--port ${this.port} is not supported: the app registration's redirect URI is fixed to localhost:3000, so Microsoft sign-in only works there.`,
        hint: "Omit --port (it defaults to 3000). If port 3000 is busy, stop the other process using it.",
      });
    }

    const out = (m: string) => { this.context.stderr.write(`  ${colors.dim(m)}\n`); };

    if (fs.existsSync(path.join(os.homedir(), ".m8t", "config.yaml"))) {
      out(colors.error("⚠ an existing ~/.m8t/config.yaml will override the onboarding app registration in apps/web — if Microsoft sign-in fails, move it aside and retry."));
    }
    const credential = new DefaultAzureCredential();
    const account = await getAzAccount();

    assertNodeVersion();

    out("waiting for Foundry (the installer's foundry-create phase)…");
    const { endpoint, accountScope } = await resolveFoundryEndpointWithWait(
      {
        credential,
        subscriptionId: state.subscriptionId,
        endpoint: typeof this.endpoint === "string" ? this.endpoint : undefined,
      },
      { onWait: out },
    );

    out("granting you Foundry data-plane access…");
    const oid = await getSignedInUserOid();
    await ensureFounderFoundryRole({ credential, subscriptionId: state.subscriptionId, principalId: oid, accountScope });

    const identity = await getSignedInUserIdentity();
    out("deploying the intake agent (azzy-intake) in the background...");
    const deployOutcome: Promise<DeployOutcome> = (async () => {
      // Runs ONCE, before the retry loop: a role-propagation retry must not
      // re-walk the ladder, re-attempt every deployment, or re-scan the sub.
      // The inventory scan is read-only and runs concurrently with the cascade.
      const [choice, inventory] = await Promise.all([
        resolveIntakeModel({
          accountScope,
          fallbackRegion: state.location,
          onNarrate: out,
        }),
        resolveSubscriptionInventory({
          subscriptionId: state.subscriptionId,
          installResourceGroup: state.resourceGroup,
          onNarrate: out,
        }),
      ]);
      return deployIntakeAgentWithRetry({
        credential,
        endpoint,
        repoRoot,
        model: choice.model,
        fieldOverrides: buildIntakeFieldOverrides({
          founderIdentityNote: composeFounderIdentityNote(identity),
          chosenModelNote: choice.note,
          inventoryNote: inventory.note,
        }),
        onWait: out,
      });
    })().then(
      (version) => ({ ok: true as const, version }),
      (error: unknown) => ({ ok: false as const, error: error instanceof Error ? error : new Error(String(error)) }),
    );

    const envPath = writeWebEnvLocal({
      repoRoot, tenantId: account.tenantId, clientId: state.appRegClientId, foundryEndpoint: endpoint,
      voice: this.voice === true,
    });

    if (!this.skipInstall) {
      out("installing apps/web dependencies (pnpm)…");
      await installWebDeps(repoRoot);
    }

    if (this.prepOnly === true) {
      const outcome = await deployOutcome;
      if (!outcome.ok) throw outcome.error;
      this.context.stdout.write(renderDeploySuccess(outcome.version, envPath));
      this.context.stdout.write(`  ${colors.hint("serve it yourself:")} pnpm --filter web dev\n`);
      return 0;
    }

    if (this.foreground === true) {
      if (this.voice === true) {
        out("starting the voice relay on :8790 in the background…");
        await serveOnboardingRelayDetached({
          repoRoot,
          foundryEndpoint: endpoint,
          tenantId: account.tenantId,
          clientId: state.appRegClientId,
          webInternalBaseUrl: "http://localhost:3000",
        });
        out("voice relay on :8790");
      }
      const outcome = await deployOutcome;
      if (!outcome.ok) {
        this.context.stderr.write(renderDeployFailure(outcome.error));
        return 1;
      }
      this.context.stdout.write(renderDeploySuccess(outcome.version, envPath));
      out("starting the webapp on :3000 (Ctrl-C to stop)…");
      await runInherit("pnpm", ["--filter", "web", "dev"], repoRoot, "BOOTSTRAP_UI_DEV_FAILED");
      return 0;
    }

    // Default: background (detached) serve — the command returns so the agent can continue.
    if (this.voice === true) {
      out("starting the voice relay on :8790 in the background…");
      await serveOnboardingRelayDetached({
        repoRoot,
        foundryEndpoint: endpoint,
        tenantId: account.tenantId,
        clientId: state.appRegClientId,
        webInternalBaseUrl: "http://localhost:3000",
      });
      out("voice relay on :8790");
    }
    out("starting the webapp on :3000 in the background…");
    const { alreadyRunning, logPath } = await serveOnboardingUiDetached({ repoRoot, port: this.port });
    if (alreadyRunning) {
      this.context.stdout.write(
        `${colors.success("✓")} chat UI already running at http://localhost:${this.port}\n`,
      );
    } else {
      this.context.stdout.write(
        `${colors.success("✓")} chat UI is live at http://localhost:${this.port}\n` +
        `  ${colors.dim(`logs: ${logPath} · stop: m8t bootstrap ui --stop`)}\n`,
      );
    }
    autoOpenOnboardingUi(`http://localhost:${this.port}`);
    const outcome = await deployOutcome;
    if (!outcome.ok) {
      this.context.stderr.write(renderDeployFailure(outcome.error));
      return 1;
    }
    this.context.stdout.write(renderDeploySuccess(outcome.version, envPath));
    return 0;
  }
}
